import Logger from '../../common/logger';
import Tracker from '../genesys/tracker';
import Cookies from '../../common/Cookies';
import { CHAT_STATE, MESSAGE_TYPE } from '../components/views/chat/actions';
import { APIMode, APISubmode } from './types';
import * as ChatActions from '../components/views/chat/actions';
import { BotMessages } from './bot/messages';
import * as Messages from './messages';
import { API_MESSAGE_TYPE } from './types';
import ApplicationContext from '../helpers/applicationcontext';

const STORAGEKEY = {
    CHATSTATE: 'oplwidget.state.chat',
    CHAT: 'oplwidget.chat',
    WINDOWID: 'oplwidget.windowid'
};

const Settings = {
    CHAT: {
        REFRESH_TIMER: 3
    },

    BOT: {
        REFRESH_TIMER: 3
    }
};

export default class Helper {
    constructor(API) {
        this.API = API;
        this.engagementID = this.getEngagementID();
        this.refreshChat = this.refreshChat.bind(this);
        this.cleanup = this.cleanup.bind(this);
        this.timeouts = {
            refreshChat: null,
            refreshBot: null
        };
    }

    open(userData) {
        const state = this.API.store.getState();

        //--Set chat mode
        if (!state.chat.mode) {
            this.API.APIMode = userData.categoryType
                ? userData.categoryType === 'max'
                    ? APIMode.CHAT
                    : userData.categoryType
                : APIMode.CHAT;
            this.API.store.dispatch(ChatActions.setAPIMode(this.API.APIMode));
        }

        // is this current session
        if (window.localStorage && !window.OPLWidgetIgnoreSessionCheck) {
            const wid = localStorage.getItem(STORAGEKEY.WINDOWID);
            if (wid !== null && wid !== this.API.windowID && !window.OPLWidgetIgnoreSessionCheck) {
                Logger.info('[APIv2] Chat already running on another window');
                Tracker.sendEvent('widgetChatMultiple');
                this.processEndOfSession(
                    false,
                    (state.chat.messages && state.chat.messages.sessionAlreadyActive) ||
                        'chatSessionAlreadyActive'
                );
                return;
            }
            localStorage.setItem(STORAGEKEY.WINDOWID, this.API.windowID);
        }

        //--Restore chat session
        const session = sessionStorage.getItem('oplwidget.chat');
        if (session) {
            Logger.log('[API] Session restored');
            // restore chat session
            this.API.session = JSON.parse(session);
            // reinit refresh timer
            this.initTimer();
            return;
        }

        //--Check if chat is finished
        if (state.chat.state !== CHAT_STATE.NOT_STARTED) {
            // chat already finished, don't restart with new session.
            // User must close chat window (will change state to CHAT_STATE.NOT_STARTED) and open new one.
            localStorage.removeItem(STORAGEKEY.CHATSTATE);
            return;
        }

        switch (this.API.APIMode) {
            case APIMode.BOT:
            case APIMode.CHAT_BOT:
                this.openChat({
                    userData: userData,
                    onSuccess: () => this.openBot(userData),
                    submode: state.chat.submode
                });
                break;
            default:
                this.openChat({ userData, submode: state.chat.submode });
        }
    }

    openChat({ userData, onSuccess, submode }) {
        if (this.API.APIMode === APIMode.CHAT) {
            const state = this.API.store.getState();
            const theme =
                state.settings.widget && state.settings.widget.theme
                    ? state.settings.widget.theme.split(' ')
                    : ['default'];
            const message =
                submode === APISubmode.MAX
                    ? 'Zaczekaj chwilę…'
                    : theme.includes('mojorange2')
                    ? 'Cześć! Za chwilę nasz Doradca dołączy do rozmowy i pomoże Ci w rejestracji konta Mój Orange. Proszę podaj numer telefonu lub usługi stacjonarnej.'
                    : 'Nie uciekaj, jeszcze chwila i zaczynamy.';
            this.API.store.dispatch(
                ChatActions.addChatEvent({
                    message: message,
                    timestamp: null,
                    localtimestamp: null,
                    id: -2,
                    from: 'system',
                    icon: 'spinner',
                    isRead: false,
                    type: ChatActions.MESSAGE_TYPE.WELCOME
                })
            );
            let chatPromo = null;
            if (state.chat.submode === APISubmode.MAX) {
                if (
                    state.settings.literals &&
                    state.settings.literals['chatPromoMax'] !== undefined
                ) {
                    const items = state.settings.literals['chatPromoMax'];
                    const idx = Math.floor(Math.random() * items.length);
                    chatPromo = items[idx];
                }
            } else {
                if (state.settings.literals && state.settings.literals['chatPromo'] !== undefined) {
                    const items = state.settings.literals['chatPromo'];
                    const idx = Math.floor(Math.random() * items.length);
                    chatPromo = items[idx];
                }
            }
            if (chatPromo) {
                this.API.store.dispatch(
                    ChatActions.addChatEvent({
                        message: chatPromo,
                        timestamp: null,
                        localtimestamp: null,
                        id: -3,
                        from: 'system',
                        isRead: false,
                        type: ChatActions.MESSAGE_TYPE.PROMO
                    })
                );
            }
        }

        //--Start new session, set required parameter nickname
        const parameters = {
            nickname: userData ? (userData.FirstName || 'User').trim() : 'User'
        };

        //--Add userData kvp
        if (userData) {
            for (const key in userData) {
                if (Object.prototype.hasOwnProperty.call(userData, key)) {
                    parameters[`userData[${key}]`] = String(userData[key]).trim();
                }
            }
        }

        Logger.log('[APIv2] Start new session');
        this.sendRequest({
            command: null,
            parameters: parameters,
            destinationAPI: APIMode.CHAT,
            callbacks: {
                success: (response) => {
                    Logger.log('[APIv2] Session started');
                    Tracker.sendEvent('widgetChatStart');
                    this.API.session = {
                        chatEnded: false,
                        chatId: response.chatId,
                        lastPosition: 0
                    };
                    this.initTimer(APIMode.CHAT);
                    this.API.store.dispatch(ChatActions.setChatState(CHAT_STATE.RUNNING));

                    this.processMessages(response);

                    this.getLimits();

                    Tracker.acceptChatInviteSuccess();
                    if (onSuccess) {
                        onSuccess();
                    }
                },
                error: (error) => {
                    if (this.API.APIMode !== APIMode.CHAT) {
                        Logger.warn('Error initializing chat API. Starting bot...');
                        Tracker.sendEvent('widgetChatInitError');
                        if (onSuccess) {
                            onSuccess();
                        }
                    } else if (error.chatEnded === true) {
                        Tracker.sendEvent('widgetChatError', JSON.stringify(error));
                        this.processEndOfSession(false, 'chatInitSessionError');
                        Logger.error('this.API.genesys.sendRequest: ' + JSON.stringify(error));
                    }
                }
            }
        });
    }

    openBot(userData) {
        Logger.log('[BOT] Start new session');
        this.API.bot.setApiUrl(userData.categoryBot);

        const getStartMessageOptions = () => {
            const options = {
                context: ApplicationContext.getApplicationIdentifier()
            };
            if (userData.userInCaseNumber) {
                options.userInCaseNumber = userData.userInCaseNumber;
            }

            const gtServerAlias = Cookies.getItem('genesys.tracker.serverAlias');
            if (gtServerAlias) {
                options.gtServerAlias = gtServerAlias;
            }

            const gtVisitId = Cookies.getItem('genesys.tracker.visitID');
            if (gtVisitId) {
                options.gtVisitId = gtVisitId;
            }

            return options;
        };

        this.sendBotRequest({
            command: 'start',
            messages: [
                {
                    type: 'cmd_start',
                    value: getStartMessageOptions()
                }
            ],
            callbacks: {
                success: (response) => {
                    Tracker.sendEvent('widgetBotStart');
                    Logger.log('[BOT] Session started');
                    this.API.store.dispatch(ChatActions.setChatState(CHAT_STATE.RUNNING));
                    this.processMessages(response);
                }
            }
        });
    }

    close() {
        const closeChat = () => {
            const state = this.API.store.getState();
            if (state.chat.state === CHAT_STATE.RUNNING) {
                this.sendChatRequest({
                    command: 'disconnect',
                    parameters: null,
                    callbacks: {
                        success: () => {
                            Tracker.sendEvent('widgetChatFinish');
                            this.processEndOfSession(true);
                        }
                    }
                });
            }
        };

        const closeBot = () => {
            const state = this.API.store.getState();
            if (state.chat.state === CHAT_STATE.RUNNING) {
                Tracker.sendEvent('widgetBotFinish');
                this.processEndOfSession(true);
            }
        };

        switch (this.API.APIMode) {
            case APIMode.BOT:
            case APIMode.CHAT_BOT:
                closeChat();
                closeBot();
                break;
            default:
                closeChat();
                break;
        }
    }

    initTimer(destinationAPI = this.API.APIMode) {
        const initChatTimer = () => {
            if (this.API && this.API.session && !this.API.session.chatEnded) {
                if (!this.timeouts.refreshChat) {
                    this.timeouts.refreshChat = setInterval(
                        this.refreshChat,
                        Settings.CHAT.REFRESH_TIMER * 1000
                    );
                }
                window.addEventListener('unload', this.cleanup, false);
            }
        };

        switch (destinationAPI) {
            case APIMode.BOT:
                break;
            default:
                initChatTimer();
                break;
        }
    }

    refresh({ messagePreview = this.API.messagePreview, destinationAPI = this.API.APIMode }) {
        if (destinationAPI !== APIMode.BOT) {
            this.refreshChat(messagePreview);
        }
    }

    refreshChat(messagePreview = this.API.messagePreview) {
        const params = {
            transcriptPosition:
                this.API.session.lastPosition === 0 ? 1 : this.API.session.lastPosition
        };

        if (messagePreview !== undefined && messagePreview !== null) {
            // don't send when no message is set
            params['message'] = messagePreview;
        }

        this.sendRequest({
            session: this.API.session,
            command: 'refresh',
            parameters: params,
            callbacks: {
                success: (response) => {
                    Logger.log('SendRequest success: ' + JSON.stringify(response));
                    this.processMessages(response);
                    if (this.API && this.API.session) {
                        this.API.session.lastPosition = response.nextPosition;
                        // save position
                        sessionStorage.setItem('oplwidget.chat', JSON.stringify(this.API.session));
                    }
                }
            },
            destinationAPI: APIMode.CHAT
        });
    }

    sendRequest({ command, parameters, callbacks, destinationAPI = this.API.APIMode }) {
        switch (destinationAPI) {
            case APIMode.BOT:
                this.sendBotRequest({
                    command,
                    callbacks,
                    messages: parameters.message
                });
                this.sendChatRequest({
                    command,
                    parameters,
                    callbacks
                });
                break;
            case APIMode.CHAT_BOT:
                this.sendBotRequest({
                    command,
                    callbacks,
                    messages: parameters.message,
                    destinationAPI: APIMode.CHAT_BOT
                });
                this.sendChatRequest({
                    command,
                    parameters,
                    callbacks,
                    destinationAPI: APIMode.CHAT_BOT
                });
                break;
            default:
                this.sendChatRequest({
                    command,
                    parameters,
                    callbacks
                });
        }
    }

    sendChatRequest({ command, parameters, callbacks, destinationAPI = this.API.APIMode }) {
        if (command === 'send') {
            this.API.messagePreview = null;
            this.clearTyping();
        }

        const requestType = Messages.getRequestType(parameters);

        if (
            requestType === API_MESSAGE_TYPE.BOT &&
            destinationAPI !== APIMode.CHAT &&
            parameters.message
        ) {
            parameters.message = BotMessages.ConvertUserBotResponse.toTextResponse(
                parameters.message
            );
        }

        if (
            this.API.session &&
            this.API.session.botMessageIndex > this.API.session.lastPosition &&
            parameters.message
        ) {
            this.API.session.lastPosition = this.API.session.botMessageIndex;
        }

        this.API.genesys.sendRequest({
            command,
            parameters,
            destinationAPI,
            session: this.API.session,
            callbacks: {
                success:
                    callbacks && callbacks.success
                        ? callbacks.success
                        : (response) => {
                              this.processMessages(response);
                              this.refresh({});
                          },
                error:
                    callbacks && callbacks.error
                        ? callbacks.error
                        : (error) => {
                              if (command === 'refresh') {
                                  Logger.log('SendRequest error: ' + JSON.stringify(error));
                                  this.processMessages(error);
                                  if (this.API && this.API.session && error.nextPosition) {
                                      this.API.session.lastPosition = error.nextPosition;
                                      // save position
                                      sessionStorage.setItem(
                                          'oplwidget.chat',
                                          JSON.stringify(this.API.session)
                                      );
                                  }
                                  return;
                              }

                              const state = this.API.store.getState();
                              if (state.chat.sessionActive) {
                                  Tracker.sendEvent('widgetChatError', JSON.stringify(error));
                              }
                              if (this.API.APIMode === APIMode.CHAT) {
                                  this.processEndOfSession(false);
                              }

                              Logger.error(
                                  'this.API.genesys.sendRequest: ' + JSON.stringify(error)
                              );
                          }
            }
        });
    }

    sendBotRequest({ command, messages, callbacks, destinationAPI = this.API.APIMode }) {
        let request;

        if (command === 'send') {
            const messageForWidget = BotMessages.ConvertUserBotResponse.toBotTextResponse(messages);
            const messageForBot = BotMessages.ConvertUserBotResponse.toBotResponses(messages);
            request = this.buildRequest(messageForBot);

            if (messageForWidget) {
                this.API.bot.addBotMessageToWidget({
                    message: JSON.stringify(messageForWidget),
                    username: 'Ty',
                    id: request.index,
                    from: 'client'
                });
            }
        } else {
            request = this.buildRequest(messages);
        }

        this.API.bot.sendRequest({
            request: request,
            callbacks: {
                success:
                    callbacks && callbacks.success
                        ? callbacks.success
                        : (response) => {
                              this.processMessages(response);
                          },
                error:
                    callbacks && callbacks.error
                        ? callbacks.error
                        : (error) => {
                              Tracker.sendEvent('widgetBotError', JSON.stringify(error));

                              switch (destinationAPI) {
                                  case APIMode.BOT:
                                      this.processEndOfSession(false, 'botApiErrorMessage');
                                      break;
                                  case APIMode.CHAT_BOT:
                                      this.transferToChat();
                                      break;
                                  default:
                                      break;
                              }
                          }
            }
        });
    }

    processMessages(response) {
        const destinationAPI =
            response && response.destinationAPI ? response.destinationAPI : this.API.APIMode;

        Logger.log(
            `processMessages: [destinationAPI=${destinationAPI}, response=${JSON.stringify(
                response
            )}]`
        );

        switch (destinationAPI) {
            case APIMode.BOT:
            case APIMode.CHAT_BOT:
                this.processChatMessages(response, destinationAPI);
                this.processBotMessages(response);
                break;
            default:
                this.processChatMessages(response, destinationAPI);
        }
    }

    processChatMessages(response, destinationAPI) {
        const responseType = Messages.getResponseType(response);

        if (responseType === API_MESSAGE_TYPE.CHAT) {
            if (destinationAPI !== APIMode.CHAT && response.nextPosition && this.API.session) {
                this.API.session.lastChatPosition = response.nextPosition;
                sessionStorage.setItem('oplwidget.chat', JSON.stringify(this.API.session));
            }

            this.API.genesys.processMessages({
                session: this.API.session,
                response: response,
                callbacks: {
                    clearTyping: () => {
                        this.clearTyping();
                    },
                    endSession: (cleanChatEnd) => {
                        this.processEndOfSession(cleanChatEnd);
                    },
                    fileUploaded: () => {
                        this.getLimits();
                    }
                },
                processText: destinationAPI === APIMode.CHAT
            });
        } else if (responseType === API_MESSAGE_TYPE.BOT && response.messages) {
            const responseMessage = BotMessages.ConvertBotResponse.toText(response.messages);

            Logger.log('BotMessage for chat: ' + responseMessage);

            if (responseMessage) {
                this.sendChatRequest({
                    command: 'send',
                    parameters: {
                        message: responseMessage,
                        messageType: 'text'
                    }
                });
            }
        }
    }

    processBotMessages(response) {
        if (this.API.APIMode === APIMode.CHAT || response.chatId) {
            return;
        }

        if (response.messages && response.messages[0].type === 'cmd_ignore') {
            return;
        }

        this.increaseBotMessageIndex();
        if (!response.index) {
            response.index = this.getBotMessageIndex();
        }

        this.API.bot.processMessages({
            response: response,
            callbacks: {
                endSession: (cleanChatEnd, message) => {
                    this.processEndOfSession(cleanChatEnd, message);
                },
                refresh: (secondsToRetry = Settings.BOT.REFRESH_TIMER) => {
                    const setBotTimeout = (seconds, callback) => {
                        Logger.log('setBotTimeout: start');
                        if (!this.API.session.chatEnded) {
                            if (!this.timeouts.refreshBot) {
                                this.timeouts.refreshBot = setTimeout(
                                    callback,
                                    parseInt(seconds) * 1000
                                );
                            }
                            window.addEventListener('onunload', this.cleanup, false);
                        }
                    };

                    setBotTimeout(secondsToRetry, () => {
                        this.timeouts.refreshBot = null;
                        this.sendBotRequest({
                            command: 'refresh',
                            messages: [
                                {
                                    type: 'rslt_refresh',
                                    value: ''
                                }
                            ],
                            callbacks: {
                                success: (response) => {
                                    this.processMessages(response);
                                }
                            }
                        });
                    });
                },
                transfer: () => {
                    this.transferToChat();
                }
            },
            destinationAPI: APIMode.BOT
        });
    }

    transferToChat() {
        if (this.API.APIMode !== APIMode.CHAT_BOT) {
            return;
        }

        this.API.genesys.sendRequestTransfer({
            session: this.API.session,
            callbacks: {
                success: () => {
                    Tracker.sendEvent('widgetBotTransferToAgent');
                    this.API.APIMode = APIMode.CHAT;
                    this.API.store.dispatch(ChatActions.setAPIMode(this.API.APIMode));
                    this.API.store.dispatch(
                        ChatActions.addChatEvent({
                            message:
                                'Witamy, za chwilę nasz Doradca dołączy do rozmowy. W trakcie oczekiwania opisz swoją sprawę.',
                            timestamp: null,
                            localtimestamp: null,
                            id: -1,
                            from: 'system',
                            icon: 'spinner',
                            isRead: false,
                            type: ChatActions.MESSAGE_TYPE.WELCOME
                        })
                    );
                },
                error: (error) => {
                    Logger.error('API.transferToChat: error=' + JSON.stringify(error));
                }
            }
        });
    }

    processEndOfSession(
        cleanEnd,
        failMessage = null // literal
    ) {
        try {
            const state = this.API.store.getState();
            this.clearTyping();

            if (
                (this.API.APIMode === APIMode.BOT || this.API.APIMode === APIMode.CHAT_BOT) &&
                cleanEnd &&
                failMessage === 'BOT'
            ) {
                this.sendBotRequest({
                    messages: [
                        {
                            type: 'rslt_finish'
                        }
                    ]
                });
            }

            if (state.chat.state === CHAT_STATE.FINISHED && state.chat.sessionActive === false) {
                this.cleanup();
                return;
            }

            if (state.chat.errors.length > 0) {
                Tracker.sendEvent('chatSessionError', {
                    reason: failMessage ?? '',
                    layerMode: state.widget.currentMode,
                    errors: state.chat.errors
                });
            }

            failMessage =
                failMessage ||
                state.chat.registration.categoryError ||
                (state.config.widget.chatOptions.messages &&
                    state.config.widget.chatOptions.messages.sessionError) ||
                'chatSessionError';
            failMessage =
                (state.settings.literals && state.settings.literals[failMessage]) ||
                'Obecnie z przyczyn technicznych, rozmowa nie może być kontynuowana. Prosimy, spróbuj ponownie.';

            this.API.store.dispatch(ChatActions.setChatState(CHAT_STATE.FINISHED));

            if (state.chat.sessionActive) {
                this.API.store.dispatch(ChatActions.setSessionActive(false));
            }

            const doAddEndingMessage = () => {
                // sprawdź, czy praca po godzinach
                const lastMessage =
                    state.chat.messages.length > 0
                        ? state.chat.messages[state.chat.messages.length - 1]
                        : null;
                if (
                    lastMessage &&
                    lastMessage.message.indexOf(
                        'Witamy. Doradcy obslugujacy czat dostepni sa w godzinach'
                    ) === 0
                )
                    return false;

                // sprawdź, czy czat nie był "czysto" zakończony
                const messages = state.chat.messages.filter((m) => m.chatEnded);
                if (
                    (messages.length === 0 && this.APIMode === APIMode.CHAT) ||
                    (!cleanEnd && this.APIMode !== APIMode.CHAT_BOT)
                ) {
                    return true;
                }

                return false;
            };

            if (doAddEndingMessage()) {
                this.API.store.dispatch(
                    ChatActions.addChatEvent({
                        message: cleanEnd ? 'Rozmowa została zakończona.' : failMessage,
                        timestamp: new Date().valueOf(),
                        localtimestamp: new Date().valueOf(),
                        id: -1,
                        from: 'system',
                        chatEnded: true,
                        type: MESSAGE_TYPE.SESSION_END,
                        failure: !cleanEnd
                    })
                );
            }
        } catch (ex) {
            Logger.log('[API] Session close failed: ' + ex);
        } finally {
            sessionStorage.removeItem(STORAGEKEY.CHAT);
            this.cleanup();
        }
    }

    sendTyping(messagePreview = null) {
        this.API.messagePreview = messagePreview;

        if (this.API.APIMode !== APIMode.CHAT) {
            return;
        }

        this.API.genesys.sendTyping({
            messagePreview: messagePreview,
            callbacks: {
                startTyping: (params) => {
                    this.sendRequest({
                        command: 'startTyping',
                        parameters: params,
                        callbacks: {
                            success: (response) => {
                                this.processMessages(response);
                            }
                        },
                        destinationAPI: APIMode.CHAT
                    });
                },
                stopTyping: () => {
                    this.sendRequest({
                        command: 'stopTyping',
                        callbacks: {
                            success: (response) => {
                                this.processMessages(response);
                            }
                        },
                        destinationAPI: APIMode.CHAT
                    });
                }
            }
        });
    }

    clearTyping() {
        if (this.API.APIMode !== APIMode.CHAT) {
            return;
        }
        this.API.genesys.clearTyping();
    }

    cleanup(destinationAPI = this.API.APIMode) {
        switch (destinationAPI) {
            case APIMode.BOT:
                this.cleanupBot();
                this.cleanupChat();
                break;
            case APIMode.CHAT_BOT:
                this.cleanupChat();
                this.cleanupBot();
                break;
            default:
                this.cleanupChat();
                break;
        }

        if (
            window.localStorage &&
            localStorage.getItem(STORAGEKEY.WINDOWID) === this.API.windowID
        ) {
            localStorage.removeItem(STORAGEKEY.WINDOWID);
        }

        this.API.session = null;
    }

    cleanupChat() {
        if (this.timeouts.refreshChat) {
            clearInterval(this.timeouts.refreshChat);
            this.timeouts.refreshChat = null;
        }

        this.API.genesys.clearTyping();
    }

    cleanupBot() {
        if (this.timeouts.refreshBot) {
            clearInterval(this.timeouts.refreshBot);
            this.timeouts.refreshBot = null;
        }
    }

    getBotMessageIndex() {
        const initBotMessageIndex = -90;

        if (!this.API.session) {
            let session = sessionStorage.getItem('oplwidget.chat');
            if (session === 'null') {
                session = null;
            }
            if (session) {
                this.API.session = JSON.parse(session);
            }
        }

        if (!this.API.session || this.API.session === 'null') {
            this.API.session = {
                botMessageIndex: initBotMessageIndex
            };
        }

        if (this.API.session && !this.API.session.botMessageIndex) {
            this.API.session.botMessageIndex = initBotMessageIndex;
        }

        return this.API.session.botMessageIndex;
    }

    increaseBotMessageIndex() {
        const newBotMessageIndex = parseInt(this.getBotMessageIndex()) + 1;

        if (newBotMessageIndex) {
            this.API.session.botMessageIndex = newBotMessageIndex;
            sessionStorage.setItem('oplwidget.chat', JSON.stringify(this.API.session));
            return newBotMessageIndex;
        } else {
            Logger.error('API.increaseBotMessageIndex: could not increase index');
        }

        return this.API.session.botMessageIndex;
    }

    buildRequest(messages) {
        return {
            engagementID: this.engagementID,
            index: this.increaseBotMessageIndex(),
            messages: messages
        };
    }

    getEngagementID() {
        const state = this.API.store.getState();
        return state.config.widget.engagementID;
    }

    getLimits() {
        if (this.API.APIMode === APIMode.BOT) {
            return;
        }
        this.sendRequest({
            command: 'file/limits',
            callbacks: {
                success: (response) => {
                    this.processMessages({
                        ...response,
                        destinationAPI: APIMode.CHAT
                    });
                }
            },
            destinationAPI: APIMode.CHAT
        });
    }

    /**
     * Upload file with File object
     *
     * @param {File} file object to upload
     */
    uploadFile(file, rejectedExtension = false) {
        function getRandomInt(min, max) {
            return Math.floor(Math.random() * (max - min + 1)) + min;
        }

        const identifier = getRandomInt(99999, 99999999);
        const time = new Date().valueOf();

        if (rejectedExtension) {
            Logger.info('Rejected forbidden extension.');

            this.API.store.dispatch(
                ChatActions.addChatEvent({
                    message: `Niedozwolony format pliku ${file.name}. `,
                    username: 'User',
                    timestamp: time,
                    localtimestamp: time,
                    id: -identifier,
                    from: 'client',
                    type: MESSAGE_TYPE.FILE_UPLOADED,
                    messageType: 'text',
                    progress: -1,
                    customMessage: {
                        'file-name': file.name,
                        'file-upload-type': 'file-system',
                        contentType: file.type,
                        'file-size': file.total
                    }
                })
            );
            return;
        }

        const state = this.API.store.getState();

        let url = [
            state.settings.env.serverURL.replace(/\/\s*$/, ''),
            this.API.genesys.session.chatId,
            'file'
        ];
        url = url.join('/');

        const form = new FormData();
        form.append('file', file);
        form.append('userId', this.API.genesys.session.userId);
        form.append('secureKey', this.API.genesys.session.secureKey);
        form.append('alias', this.API.genesys.session.alias);
        form.append('userData[file-description]', file.name);

        const xhr = new XMLHttpRequest();
        xhr.onload = () => {
            const response = JSON.parse(xhr.responseText);
            Logger.log(`[Storage] Response: ${response}`);
            if (
                xhr.status === 200 &&
                (response.errors === undefined || response.errors.length === 0)
            ) {
                Logger.log(`[Storage] Finished sending file: ${file.name} [id=${identifier}]`);
                this.API.store.dispatch(
                    ChatActions.updateChatEvent({
                        message: `Dodano załącznik: ${file.name}`,
                        id: -identifier,
                        progress: 100,
                        hidden: true,
                        customMessage: {
                            'file-id': response.userData['file-id'],
                            'file-name': file.name,
                            'file-upload-type': 'file-system',
                            contentType: file.type,
                            'file-size': file.total
                        }
                    })
                );
            } else {
                let message = '';
                let code = -1;
                response.errors.forEach((error) => {
                    code = error.code;
                    switch (error.code) {
                        case 201:
                            message += 'Osiągnięty został limit ilości przesłanych plików.';
                            break;
                        case 202:
                            message += 'Osiągnięty został limit rozmiaru przesłanych plików.';
                            break;
                        case 203:
                            message += 'Plik jest niewłaściwego typu.';
                            break;
                        case 261:
                            message += 'Aby przesłać plik zaczekaj na połączenie z doradcą.';
                            break;
                        case 999:
                            message += 'Plik został odrzucony.';
                            Tracker.sendEvent('widgetSecurity', { supportId: error.supportId });
                            break;
                        default:
                            break;
                    }
                });
                Logger.warn(
                    `[Storage] Error sending file: ${file.name} [id=${identifier}, code=${code}]`
                );
                this.API.store.dispatch(
                    ChatActions.updateChatEvent({
                        message: `Nie można przesłać pliku ${file.name}. ${message}`,
                        id: -identifier,
                        progress: -1
                    })
                );
                this.getLimits();
            }
        };

        xhr.onerror = () => {
            Logger.warn(`[Storage] Error sending file: ${file.name} [id=${identifier}]`);
            this.API.store.dispatch(
                ChatActions.updateChatEvent({
                    // message: `Błąd przesyłania pliku ${file.name}. Błąd ${xhr.status}`,
                    message: `Błąd przesyłania pliku ${file.name}.`,
                    id: -identifier,
                    progress: -1
                })
            );
            this.getLimits();
        };
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Accept', '*/*');
        xhr.send(form);

        Logger.log(`[Storage] Send file: ${file.name} [id=${identifier}]`);

        this.API.store.dispatch(
            ChatActions.addChatEvent({
                message: `Przesyłam plik ${file.name}`,
                username: 'User',
                timestamp: time,
                localtimestamp: time,
                id: -identifier,
                from: 'client',
                type: MESSAGE_TYPE.FILE_UPLOADED,
                messageType: 'text',
                progress: 50,
                customMessage: {
                    'file-name': file.name,
                    'file-upload-type': 'file-system',
                    contentType: file.type,
                    'file-size': file.total
                }
            })
        );
    }

    /**
     * Download file from Genesys
     *
     * @param {string} identifier
     */
    downloadFile(identifier) {
        const url = ['file', identifier, 'download'];

        this.API.genesys.sendDownloadRequest({
            session: this.API.session,
            command: url.join('/'),
            parameters: { acceptBinary: true },
            callbacks: {
                success: () => {
                    this.getLimits();
                },
                error: (e) => {
                    this.getLimits();
                    Logger.error(e.advice);
                    window.alert(e.advice);
                }
            }
        });
    }
}
